import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { errorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { AlertIcon } from '@/shared/ui/icons';
import { Logo } from '@/shared/ui/Logo';
import { PasswordInput } from '@/shared/ui/PasswordInput';

import { useLogin } from './useSession';

interface RedirectState {
  from?: string;
}

/**
 * The door. On a desktop, half of it is the form and the other half is the logo on the
 * dark wall of the shop, and nothing else: whoever gets here already knows what this is.
 * On a phone it is the logo and the form.
 *
 * Nobody is named: the person who hands out passwords is «quien administra la
 * aplicación», whoever that is the day this is read. And there is no «he olvidado mi
 * contraseña» on purpose: the password comes from that person, in hand or as an
 * invitation link they issue from the panel (`/invitacion/:token`), never from a
 * self-service flow on the door.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const redirectTo = (location.state as RedirectState | null)?.from ?? '/correos';

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          void navigate(redirectTo, { replace: true });
        },
      },
    );
  }

  return (
    <main className="bg-paper grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <section className="bg-nav relative hidden items-center justify-center overflow-hidden p-12 lg:flex">
        {/* A grid of nuts, faint, like the wall of the shop. Pure CSS, no image. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1.2px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <Logo inverted className="relative h-28 w-auto" />
      </section>

      <section className="flex items-center justify-center px-5 py-10 sm:px-6 sm:py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo className="h-14" />
          </div>
          <h2 className="font-display text-ink text-[26px] font-semibold">Entrar</h2>
          <p className="text-ink-3 mt-1 text-[15px]">
            Con el correo y la contraseña que te dio quien administra la aplicación.
          </p>

          <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4" noValidate>
            <Input
              label="Correo electrónico"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <PasswordInput
              label="Contraseña"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            {login.isError ? (
              <p
                role="alert"
                className="bg-danger-soft text-danger border-danger/20 flex items-start gap-2 rounded-md border px-3 py-2 text-[14px]"
              >
                <AlertIcon size={16} className="mt-0.5 shrink-0" />
                <span>{errorMessage(login.error)}</span>
              </p>
            ) : null}

            <Button type="submit" size="lg" disabled={login.isPending} className="mt-1">
              {login.isPending ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
        </div>
      </section>
    </main>
  );
}
